import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  MessageSquare,
  Clock,
  Users,
  TrendingUp,
  Activity,
  CheckCircle,
  AlertCircle,
  Play
} from 'lucide-react';

// MetricCard component defined outside Dashboard to prevent re-creation on renders
const MetricCard = ({ icon: Icon, label, value, subtitle, trend, color = "blue" }) => {
  const colorClasses = {
    blue: { icon: "bg-blue-100 text-blue-600", border: "border-blue-100", gradient: "from-blue-50", textHover: "group-hover:text-blue-600" },
    green: { icon: "bg-green-100 text-green-600", border: "border-green-100", gradient: "from-green-50", textHover: "group-hover:text-green-600" },
    purple: { icon: "bg-purple-100 text-purple-600", border: "border-purple-100", gradient: "from-purple-50", textHover: "group-hover:text-purple-600" },
    orange: { icon: "bg-orange-100 text-orange-600", border: "border-orange-100", gradient: "from-orange-50", textHover: "group-hover:text-orange-600" }
  };

  const colors = colorClasses[color];

  return (
    <Card className={`p-6 border-2 ${colors.border} bg-gradient-to-br ${colors.gradient} to-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-3 rounded-xl ${colors.icon}`}>
              <Icon className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          </div>
          <div className="space-y-1">
            <h3 className={`text-3xl font-bold text-gray-900 ${colors.textHover} transition-colors duration-300`}>{value}</h3>
            {subtitle && (
              <p className="text-sm text-gray-500">{subtitle}</p>
            )}
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-lg">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-semibold">{trend}</span>
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * Professional Dashboard Component
 * Shows key metrics and analytics similar to livechat.com
 */
const Dashboard = ({ sessions = [], agent }) => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({
    totalChats: 0,
    activeChats: 0,
    avgResponseTime: 0,
    satisfactionRate: 0,
    todayChats: 0,
    resolvedChats: 0
  });

  useEffect(() => {
    calculateMetrics();
  }, [sessions]);

  const calculateMetrics = () => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const activeChats = sessions.filter(s =>
      s.status === 'active' || s.status === 'waiting_for_agent' || s.status === 'agent_active'
    ).length;

    const todayChats = sessions.filter(s =>
      new Date(s.created_at) >= todayStart
    ).length;

    const resolvedChats = sessions.filter(s =>
      s.status === 'completed' || s.status === 'closed'
    ).length;

    // Calculate average response time (mock for now)
    const avgResponseTime = sessions.length > 0 ? 2.5 : 0;

    // Calculate satisfaction rate (mock for now)
    const satisfactionRate = sessions.length > 0 ? 94 : 0;

    setMetrics({
      totalChats: sessions.length,
      activeChats,
      avgResponseTime,
      satisfactionRate,
      todayChats,
      resolvedChats
    });
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Welcome back, {agent?.name || 'Agent'}!
            </h1>
            <p className="text-blue-100 text-lg">
              Here's what's happening with your conversations today
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/demo')}
              className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-6 py-2 flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              View Demo
            </Button>
            <div className="hidden lg:flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <Activity className="w-5 h-5" />
              <span className="font-medium">Live Dashboard</span>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          icon={MessageSquare}
          label="Total Chats"
          value={metrics.totalChats}
          subtitle="All time conversations"
          color="blue"
        />

        <MetricCard
          icon={Activity}
          label="Active Now"
          value={metrics.activeChats}
          subtitle="Ongoing conversations"
          trend="+12%"
          color="green"
        />

        <MetricCard
          icon={Clock}
          label="Avg Response Time"
          value={`${metrics.avgResponseTime} min`}
          subtitle="Last 30 days"
          color="purple"
        />

        <MetricCard
          icon={CheckCircle}
          label="Satisfaction Rate"
          value={`${metrics.satisfactionRate}%`}
          subtitle="Customer happiness"
          trend="+5%"
          color="orange"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl group-hover:scale-110 group-hover:bg-blue-200 transition-all duration-300">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Today's Chats</p>
              <h4 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">{metrics.todayChats}</h4>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-2 border-green-100 bg-gradient-to-br from-green-50 to-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-xl group-hover:scale-110 group-hover:bg-green-200 transition-all duration-300">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Resolved</p>
              <h4 className="text-2xl font-bold text-gray-900 group-hover:text-green-600 transition-colors duration-300">{metrics.resolvedChats}</h4>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-2 border-orange-100 bg-gradient-to-br from-orange-50 to-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-xl group-hover:scale-110 group-hover:bg-orange-200 transition-all duration-300">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Pending</p>
              <h4 className="text-2xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-300">{metrics.activeChats}</h4>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
